// Shared decode-resolution state machine for the map peek popover (T11) and the expanded-dialog map
// pane. It resolves the value to decode (awaiting a windowed `fetchFullValue` when supplied, so a
// truncated 1 KB preview is never decoded), then decodes through the ASYNC lazy API only
// (`decodeSpatialBatch` / `loadSpatialDecoder` — the sync core is never imported by UI code).
//
// Over-cap ladder (spec §Task 11): the eager pass decodes at MAX_EAGER_DECODE_BYTES. A value over
// that cap does NOT auto-decode — it surfaces `over-cap` with a "Load full geometry (N MB)" button
// (loadFull → re-decode at MAX_ONDEMAND_DECODE_BYTES). A value over the 64 MB on-demand cap is a
// terminal `too-large` state (no load button). Every other failure is `error`.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialColumnDeclaration } from '../../common/utils/spatialValueAccessor';
import type { PlanarTransform, SpatialMapFeature } from './spatialMapTypes';
import { decodeSpatialBatch } from '../decode/decodeSpatialBatch';
import { loadSpatialDecoder } from '../decode/loadSpatialDecoder';
import { MAX_EAGER_DECODE_BYTES, MAX_ONDEMAND_DECODE_BYTES } from '../decode/decodeLimits';
// proj4-free surface helpers (the proj4 ladder itself arrives lazily via loadSpatialDecoder).
import { projectFeaturesForMap } from '../projection/sridProjection';
import { fetchDbSridDef } from '../projection/sridDefsProvider';
import type { SridDefCache } from '../projection/sridLadder';
import { stableSpatialValueKey } from './spatialValueKey';

// One SRID def cache per connection (positive + negative), shared across peeks so a DB lookup for a
// given SRID happens at most once per connection per session.
const sridCaches = new Map<string, SridDefCache>();
function sridCacheFor(connectionId: string | undefined): SridDefCache {
  if (!connectionId) return new Map();
  let cache = sridCaches.get(connectionId);
  if (!cache) {
    cache = new Map();
    sridCaches.set(connectionId, cache);
  }
  return cache;
}

export type SpatialPreviewState =
  | { status: 'loading' }
  | {
      status: 'ready';
      feature: SpatialMapFeature; // ORIGINAL decoded geometry (summary + copy use this)
      mapFeatures: SpatialMapFeature[]; // reprojected-to-WGS84 (geographic) or raw (planar) for the map
      summary: string;
      value: unknown;
      planarTransform: PlanarTransform | null;
      projectionNote: string | null;
      // T34a — the decoded geometry was a curve; the map shows the linearized display shape while
      // Copy WKT / export keep the exact arcs. Drives the "arc approximated for display" hint.
      curveApproximated: boolean;
    }
  | { status: 'over-cap'; sizeMb: string }
  | { status: 'too-large'; sizeMb: string }
  | { status: 'error'; message: string };

const REASON_TEXT: Record<string, string> = {
  'unsupported-curve': 'Curved geometry is not supported yet.',
  'parse-error': 'Could not parse this geometry.',
  unrecognized: 'Unrecognized geometry format.',
};

const NOT_FETCHED = Symbol('not-fetched');

export function useSpatialGeometryPreview(args: {
  value: unknown;
  engine: DatabaseEngineId;
  dataType?: string;
  /** T18 — routes the decode through the declared arm (`decodeDeclaredSpatialValue`). */
  declaration?: SpatialColumnDeclaration;
  fetchFullValue?: () => Promise<unknown>;
  enabled: boolean;
  /** When present, non-bundled SRIDs are resolved from this connection's `spatial_ref_sys`. */
  connectionId?: string;
  database?: string;
}): { state: SpatialPreviewState; loadFull: () => void } {
  const { value, engine, dataType, declaration, fetchFullValue, enabled, connectionId, database } = args;
  const declKey = declaration
    ? `${declaration.id}:${declaration.format}:${declaration.lngColumn ?? ''}:${declaration.axisOrder ?? ''}`
    : '';
  // Oracle (`__jamSdoGeometry`) and MSSQL (`__jamMssqlGeometry`) hand the renderer an OBJECT, so a
  // parent re-render that re-creates an EQUAL marker used to change `decodeAt`'s identity and
  // re-run the decode — dropping the surface back to `loading`, which unmounts the map and the Copy
  // buttons mid-interaction. The content key re-triggers on a real value change (the expanded
  // dialog swaps cells while mounted) but not on a bare re-render. See `spatialValueKey.ts`.
  const valueKey = stableSpatialValueKey(value);
  const [state, setState] = useState<SpatialPreviewState>({ status: 'loading' });
  const fullRef = useRef<unknown>(NOT_FETCHED);
  const runIdRef = useRef(0);
  // Held in a ref so a fresh closure each parent render never re-triggers the
  // decode effect (which keys off `decodeAt`); the value/engine/dataType inputs
  // are stable while a peek/dialog is open, so the effect runs once per open.
  const fetchFullValueRef = useRef(fetchFullValue);
  fetchFullValueRef.current = fetchFullValue;
  // Same reason as `fetchFullValueRef`: read the live value without making object identity a dep.
  const valueRef = useRef(value);
  valueRef.current = value;

  const decodeAt = useCallback(
    async (maxBytes: number) => {
      const myId = ++runIdRef.current;
      setState({ status: 'loading' });
      try {
        if (fullRef.current === NOT_FETCHED) {
          const fetch = fetchFullValueRef.current;
          fullRef.current = fetch ? await fetch() : valueRef.current;
        }
        const full = fullRef.current;
        const [{ result }] = await decodeSpatialBatch(
          [{ key: 0, engine, value: full, dataType, declaration }],
          { maxBytesPerValue: maxBytes },
        );
        if (runIdRef.current !== myId) return;
        if (result.ok) {
          const { formatSpatialSummary, resolveSridMap } = await loadSpatialDecoder();
          if (runIdRef.current !== myId) return;
          const feature: SpatialMapFeature = { id: 0, geometry: result.geometry };
          // Resolve the geometry's SRID (bounded DB lookup for non-bundled codes) and reproject to
          // WGS84 for the basemap, or hand back a planar transform + note when it can't be resolved.
          const fetchDef = connectionId
            ? (srid: number) => fetchDbSridDef(engine, connectionId, database ?? '', srid)
            : undefined;
          const resolutions = await resolveSridMap([result.geometry.srid], {
            fetchDef,
            cache: sridCacheFor(connectionId),
          });
          if (runIdRef.current !== myId) return;
          const projected = projectFeaturesForMap([feature], resolutions);
          setState({
            status: 'ready',
            feature,
            mapFeatures: projected.features,
            summary: formatSpatialSummary(result.geometry),
            value: full,
            planarTransform: projected.planarTransform,
            projectionNote: projected.projectionNote,
            curveApproximated: result.geometry.curved === true,
          });
          return;
        }
        if (result.reason === 'too-large') {
          // Over the on-demand cap (or already decoding at it) → terminal; otherwise offer load-full.
          const terminal = maxBytes >= MAX_ONDEMAND_DECODE_BYTES || parseFloat(result.message) > 64;
          setState({ status: terminal ? 'too-large' : 'over-cap', sizeMb: result.message });
          return;
        }
        setState({ status: 'error', message: REASON_TEXT[result.reason] ?? 'Could not render geometry.' });
      } catch {
        if (runIdRef.current === myId) {
          setState({ status: 'error', message: 'Could not render geometry.' });
        }
      }
    },
    // declKey stands in for the declaration object, valueKey for the cell value (both stable
    // across renders; the raw objects are read from refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, dataType, valueKey, connectionId, database, declKey],
  );

  // Eager decode whenever the surface enables (popover opens) or the value changes.
  useEffect(() => {
    if (!enabled) return;
    fullRef.current = NOT_FETCHED;
    void decodeAt(MAX_EAGER_DECODE_BYTES);
    return () => {
      runIdRef.current += 1; // cancel any in-flight decode on close/unmount
    };
  }, [enabled, decodeAt]);

  const loadFull = useCallback(() => {
    void decodeAt(MAX_ONDEMAND_DECODE_BYTES);
  }, [decodeAt]);

  return { state, loadFull };
}
